import axios, { AxiosRequestConfig } from 'axios'
import router from '@/router/index'
import { httpStatus } from './http-status'
import { useUserStore } from '@/store/modules/user'
import { ElMessage } from 'element-plus'
import { usePermissionStore } from '@/store/modules/permission'
import { useStatisticsStore } from '@/store/modules/statistics'
import qs from 'qs'
import { getClientMac } from '@/libs/utils/client'
axios.defaults.headers.post['Content-Type'] = 'application/json;charset=UTF-8'
axios.defaults.headers.common['Remote-Host'] =
  import.meta.env.VITE_APP_BASE_DOMAIN || window.location.hostname

// 登录失效标志，防止重复提示
let isTokenExpired = false
// 多个请求同时过期时共享同一次刷新请求
let refreshTokenPromise: Promise<string> | null = null
// AbortController用于取消所有请求
let abortController = new AbortController()

type RetryRequestConfig = AxiosRequestConfig & {
  _retry?: boolean
}

const baseURL = `${import.meta.env.VITE_APP_BASE_API}${
  import.meta.env.VITE_API_URL
}`

const http = axios.create({
  baseURL,
  timeout: 30000,
  // 自定义参数序列化函数
  paramsSerializer: params => {
    return qs.stringify(params, { allowDots: true })
  },
})

const statisticsApiPrefixes = [
  '/clinic/statistics/',
  '/clinic/statistics-charges-details-item/',
  '/clinic/operational-analytics/',
  '/clinic/inventory-statistics/',
  '/clinic/commission-scheme/',
  '/clinic/doctor-evaluation/',
]

// 刷新 token 使用独立实例，避免刷新接口再次进入 401 拦截器造成死循环
const refreshHttp = axios.create({
  baseURL,
  timeout: 30000,
})

const expireLogin = () => {
  if (isTokenExpired) return

  isTokenExpired = true
  abortController.abort()
  abortController = new AbortController()

  const userStore = useUserStore()
  const permissionStore = usePermissionStore()
  userStore.removeToken()
  permissionStore.initRoutesAsync([])
  ElMessage.warning('登录失效，请重新登录')

  try {
    router.push({ path: '/loginPage' })
  } catch (error) {
    window.location.reload()
  }
}

const refreshAccessToken = () => {
  if (refreshTokenPromise) return refreshTokenPromise

  const userStore = useUserStore()
  const refreshToken = userStore.getRefreshToken
  if (!refreshToken) {
    expireLogin()
    return Promise.reject(new Error('登录失效'))
  }

  refreshTokenPromise = refreshHttp
    .post('/system/auth/refresh-token', undefined, {
      params: { refreshToken },
      headers: {
        'Client-Type': 'pc',
        'Tenant-Id': userStore.getTenant,
        'Dept-Id': userStore.getDept,
      },
    })
    .then(response => {
      const result = response.data
      if (result?.code !== 0 || !result?.data?.accessToken) {
        return Promise.reject(
          new Error(result?.msg || result?.message || '刷新 token 失败')
        )
      }

      userStore.setTokens(
        result.data.accessToken,
        result.data.refreshToken || refreshToken
      )
      isTokenExpired = false
      return result.data.accessToken as string
    })
    .catch(error => {
      expireLogin()
      return Promise.reject(error)
    })
    .finally(() => {
      refreshTokenPromise = null
    })

  return refreshTokenPromise
}

const retryAfterRefresh = (config: RetryRequestConfig) => {
  if (config._retry) {
    expireLogin()
    return Promise.reject({ code: 401, message: '登录失效' })
  }

  config._retry = true
  return refreshAccessToken().then(() => http(config))
}

// 请求拦截器
http.interceptors.request.use(
  async function (config: AxiosRequestConfig) {
    // 为每个请求添加signal，用于取消请求
    config.signal = abortController.signal

    if (config.headers) {
      const userStore = useUserStore()
      if (userStore.getToken && isTokenExpired) {
        isTokenExpired = false
      }
      config.headers.Authorization = userStore.getToken
      config.headers['Tenant-Id'] = userStore.getTenant
      config.headers['Dept-Id'] = userStore.getDept
      config.headers['Client-Type'] = 'pc'
      const clientMac = await getClientMac()
      if (clientMac) config.headers['Client-Mac'] = clientMac
    }
    const statisticsStore = useStatisticsStore()
    if (
      statisticsStore.active &&
      config.method?.toLowerCase() === 'get' &&
      statisticsApiPrefixes.some(prefix => config.url?.startsWith(prefix))
    ) {
      config.params = {
        ...(config.params || {}),
        statisticsScope: true,
        deptId: statisticsStore.selectedDeptId ?? undefined,
      }
    }
    return config
  },
  error => {
    console.log(error.data.error.message)
    return Promise.reject(error.data.error.message)
  }
)

http.interceptors.response.use(
  response => {
    const status = response.status
    const resData = response
    const config = response.config || {}
    if (status === 200) {
      const statisticsStore = useStatisticsStore()
      if (statisticsStore.active && config.params?.statisticsScope) {
        // 统计接口只返回 deptId，门店名称统一使用统计页已授权门店列表补齐。
        statisticsStore.fillStoreNames(resData.data)
      }
      //TODO:需要更改登录失效的code码
      if (!resData.data?.code) {
        return resData
      }
      if (resData.data.code === 401) {
        return retryAfterRefresh(config)
      } else if (resData?.data.code !== 0) {
        if (config?.noErrorTip) {
          //通信异常-但不做提示
          const { msg, message, ...resResult } = resData.data
          return Promise.reject({
            ...resResult,
            message: msg || message,
          })
        }
        if (resData?.data.msg && resData?.data.msg.length > 0) {
          ElMessage.error(resData?.data.msg ?? '请求错误')
        }
        //通信异常
        const { msg, message, ...resResult } = resData.data
        return Promise.reject({
          ...resResult,
          message: msg || message,
        })
      } else {
        return resData
      }
    } else {
      ElMessage.error('请求失败')
      console.error('接口返回错误：', response)
      return Promise.reject(response)
    }
  },
  function (error) {
    console.error('axios接口返回错误：', error)

    // 如果是取消请求导致的错误，不显示错误提示
    if (axios.isCancel(error) || error.name === 'CanceledError') {
      console.log('请求已取消:', error.message)
      return Promise.reject(error)
    }

    // 处理 401 未授权错误
    if (error.response && error.response.status === 401) {
      return retryAfterRefresh(error.config || {})
    }

    // 如果是token失效导致的错误，不再重复提示
    if (!isTokenExpired) {
      if (error.message.includes('timeout')) {
        ElMessage.error(`网络连接异常，请求超时，请稍后再次尝试`)
      } else {
        ElMessage.error(`${error.message}` || '请求失败')
      }
    }
    return Promise.reject(error)
  }
)

export default http
